import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Box, Stack } from '@mui/material';
import { DocumentStatus, SalesOrderStatus, UserRole } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaginatedResponse } from '@synergy/api';
import {
    PaloAmount,
    PaloColumn,
    PaloConfirmationDialog,
    PaloListView,
    PaloNotice,
    PaloStatusPill,
} from '@synergy/components-palo';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { ValueOrNDash } from '@synergy/components';
import { hasRole } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import { APPROVALS_CHANGED } from '../use-pending-approval-count';
import { OrderApproval } from '../models/order-approval';
import { PaloApprovalCard } from './palo-approval-card';
import { PaloApprovalItems } from './palo-approval-items';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const EMPTY: PaginatedResponse<OrderApproval> = { items: [], total: 0, page: 1, limit: 50 };
const DECIDED = [SalesOrderStatus.CompletedWithApproval, SalesOrderStatus.Declined].join(',');

type Decision = { approval: OrderApproval; approve: boolean };

/**
 * Order approval in the member portal (#2281): what waits for the shop
 * admin stands on top, so it is not missed; what was decided follows as a
 * list. Approving and declining work as before, the whole cart at once —
 * partial approval comes with #2240.
 */
export const PaloOrderApprovals = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const user = useSelector(state => state.user);
    const canDecide = hasRole(UserRole.ShopAdmin, user);
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState<OrderApproval[]>([]);
    const [decided, setDecided] = useState<PaginatedResponse<OrderApproval>>(EMPTY);
    const [requesting, setRequesting] = useState(true);
    const [expandedId, setExpandedId] = useState<number>();
    const [decision, setDecision] = useState<Decision>();

    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? 50);

    const load = useCallback(() => {
        setRequesting(true);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const [pending, done] = await Promise.all([
                api.orderApprovals.cget(
                    new URLSearchParams({
                        statuses: SalesOrderStatus.CartPending,
                        sort: 'orderDate',
                        order: 'asc',
                        limit: '100',
                    })
                ),
                api.orderApprovals.cget(
                    new URLSearchParams({
                        statuses: DECIDED,
                        sort: 'orderDate',
                        order: 'desc',
                        page: String(page),
                        limit: String(limit),
                    })
                ),
            ]);

            setOpen(pending.success ? pending.result.items : []);
            setDecided(done.success ? done.result : EMPTY);
            setRequesting(false);
        });
    }, [dispatch, page, limit]);

    useEffect(load, [load]);

    const decide = (): void => {
        if (!decision) {
            return;
        }

        const { approval, approve } = decision;
        setDecision(undefined);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = approve
                ? await api.orderApprovals.approve(approval.id)
                : await api.orderApprovals.decline(approval.id);

            dispatch(
                Snackbar.enqueue(
                    result.success
                        ? Snackbar.PoolNotification.success(
                              approve ? 'app.orderApprovals.approved' : 'app.orderApprovals.declined',
                              {
                                  number: approval.number,
                              }
                          )
                        : Snackbar.PoolNotification.error('app.orderApprovals.failed')
                )
            );
            load();
            window.dispatchEvent(new Event(APPROVALS_CHANGED));
        });
    };

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const columns: PaloColumn<OrderApproval>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: a => a.number,
        },
        {
            key: 'orderDate',
            type: 'date',
            head: <FormattedMessage id="app.orders.column.orderDate" />,
            cell: a => (
                <ValueOrNDash
                    value={a.orderDate}
                    valueRender={value => <FormattedDate value={value} year="numeric" month="short" day="2-digit" />}
                    strictEqual={true}
                />
            ),
        },
        {
            key: 'customerContact',
            type: 'name',
            head: <FormattedMessage id="app.orders.column.orderer" />,
            cell: a => <ValueOrNDash value={a.customerContact?.fullName} />,
        },
        {
            key: 'items',
            type: 'count',
            head: <FormattedMessage id="app.orders.column.items" />,
            cell: a => a.itemCount,
        },
        {
            key: 'amount',
            type: 'amount',
            currency: decided.items[0]?.totalPrice.currency,
            head: <FormattedMessage id="app.orders.column.net" />,
            cell: a => <PaloAmount value={a.totalNetPrice} />,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: a => (
                <PaloStatusPill
                    status={SalesOrderStatus.Declined === a.status ? DocumentStatus.Canceled : DocumentStatus.Completed}
                    label={
                        <FormattedMessage
                            id={
                                SalesOrderStatus.Declined === a.status
                                    ? 'app.orderApprovals.status.declined'
                                    : 'app.orderApprovals.status.approved'
                            }
                        />
                    }
                />
            ),
        },
        {
            key: 'checkedBy',
            type: 'classification',
            head: <FormattedMessage id="app.orderApprovals.decidedBy" />,
            variant: 'muted',
            cell: a => <ValueOrNDash value={a.checkedBy?.fullName} />,
        },
    ];

    return (
        <PaloListView<OrderApproval, number>
            title={<FormattedMessage id="app.navigation.orderApproval" />}
            notice={
                <Stack gap="10px" sx={{ mb: '14px' }}>
                    <PaloNotice tone="info">
                        <FormattedMessage id="app.orderApprovals.hint" />
                    </PaloNotice>
                    <Box sx={{ fontSize: '13px', fontWeight: 650, color: tokens.color.ink }}>
                        {/* The orderer sees the same carts without being the one to
                            decide; "deine Freigabe" read as if it were theirs (R-160). */}
                        <FormattedMessage
                            id={canDecide ? 'app.orderApprovals.waiting' : 'app.orderApprovals.waitingForAdmin'}
                            values={{ count: open.length }}
                        />
                    </Box>
                    {0 === open.length && !requesting && (
                        <Box sx={{ fontSize: '12.5px', color: tokens.color.ink3 }}>
                            <FormattedMessage id="app.orderApprovals.nothingWaiting" />
                        </Box>
                    )}
                    {open.map(approval => (
                        <PaloApprovalCard
                            key={approval.id}
                            approval={approval}
                            canDecide={canDecide}
                            onApprove={() => setDecision({ approval, approve: true })}
                            onDecline={() => setDecision({ approval, approve: false })}
                        />
                    ))}
                    <Box sx={{ fontSize: '13px', fontWeight: 650, color: tokens.color.ink, mt: '6px' }}>
                        <FormattedMessage id="app.orderApprovals.done" />
                    </Box>
                </Stack>
            }
            columns={columns}
            items={decided.items}
            getRowId={a => a.id}
            requesting={requesting}
            onRowClick={a => setExpandedId(current => (current === a.id ? undefined : a.id))}
            expandedRowId={expandedId}
            renderExpanded={a => <PaloApprovalItems id={a.id} currency={a.totalPrice.currency} />}
            emptyLabel={<FormattedMessage id="app.orderApprovals.empty" />}
            pagination={{
                count: decided.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <PaloConfirmationDialog
                isVisible={!!decision}
                isSuccessDialog={!!decision?.approve}
                title={
                    <FormattedMessage
                        id={decision?.approve ? 'app.orderApprovals.approve.title' : 'app.orderApprovals.decline.title'}
                    />
                }
                action={
                    <FormattedMessage
                        id={decision?.approve ? 'app.orderApprovals.approve' : 'app.orderApprovals.decline'}
                    />
                }
                onConfirm={decide}
                onCancel={() => setDecision(undefined)}
                cancelText={<FormattedMessage id="app.common.cancel" />}
            >
                {/* No mail to promise where the orderer has been deleted since:
                    the name stays on the order, the person does not (R-177). */}
                <FormattedMessage
                    id={`app.orderApprovals.${decision?.approve ? 'approve' : 'decline'}.text${
                        false === decision?.approval.ordererGetsMail ? 'NoMail' : ''
                    }`}
                    values={{
                        number: decision?.approval.number,
                        orderer: decision?.approval.customerContact?.fullName ?? '–',
                    }}
                />
            </PaloConfirmationDialog>
        </PaloListView>
    );
};
